import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Modal that lists either Followers or Following for a given user.
 *
 * Props:
 *   open, onOpenChange — Dialog control
 *   userId            — whose list to fetch
 *   mode              — "followers" | "following"
 *   ownerHandle       — username/display name shown in the title
 */
export default function FollowListModal({ open, onOpenChange, userId, mode, ownerHandle }) {
  const { user: viewer } = useAuth() || {};
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setItems(null);
    (async () => {
      try {
        const path = mode === "following" ? "following" : "followers";
        const { data } = await api.get(`/users/${userId}/${path}`);
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) {
          setItems([]);
          toast.error(apiError(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId, mode]);

  const toggleFollow = async (row) => {
    if (!viewer) {
      toast.error("Log in to follow people.");
      return;
    }
    if (row.is_self || busyId === row.user_id) return;
    setBusyId(row.user_id);
    try {
      const { data } = row.is_following
        ? await api.delete(`/sellers/${row.user_id}/follow`)
        : await api.post(`/sellers/${row.user_id}/follow`);
      setItems((prev) =>
        (prev || []).map((r) =>
          r.user_id === row.user_id ? { ...r, is_following: data.is_following } : r
        )
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const title =
    mode === "following"
      ? `People @${ownerHandle || "user"} follows`
      : `People following @${ownerHandle || "user"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-200">
          <DialogTitle className="text-base font-display font-black tracking-tight">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div
          className="max-h-[60vh] overflow-y-auto"
          data-testid={`follow-list-modal-${mode}`}
        >
          {items === null ? (
            <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-zinc-700">
                {mode === "following"
                  ? "Not following anyone yet."
                  : "No followers yet."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {items.map((row) => (
                <li
                  key={row.user_id}
                  className="flex items-center gap-3 px-5 py-3"
                  data-testid={`follow-list-row-${row.user_id}`}
                >
                  <Link
                    to={`/seller/${row.user_id}`}
                    onClick={() => onOpenChange(false)}
                    className="flex items-center gap-3 flex-1 min-w-0 group"
                  >
                    <div className="h-10 w-10 rounded-full bg-zinc-100 overflow-hidden flex-shrink-0">
                      {row.picture ? (
                        <img src={row.picture} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full grid place-items-center text-zinc-400 font-mono text-xs">
                          @
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm text-zinc-950 truncate group-hover:text-[#1D78AB]">
                        @{row.username || "user"}
                        {row.is_founder && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold tracking-wider">
                            FOUNDER
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        {row.is_seller ? "Seller" : "Buyer"}
                        {row.verified && " · Verified"}
                      </div>
                    </div>
                  </Link>
                  {!row.is_self && viewer && (
                    <button
                      onClick={() => toggleFollow(row)}
                      disabled={busyId === row.user_id}
                      className={`h-8 px-3 rounded-full text-xs font-bold transition-colors disabled:opacity-50 ${
                        row.is_following
                          ? "bg-zinc-100 text-zinc-600 border border-zinc-300 hover:bg-zinc-200"
                          : "bg-[#1D78AB] text-white border border-[#1D78AB] hover:bg-[#165d85]"
                      }`}
                      data-testid={`follow-list-toggle-${row.user_id}`}
                    >
                      {busyId === row.user_id
                        ? "…"
                        : row.is_following
                        ? "Following"
                        : "Follow"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
